// 26 ADM-006: admin users and roles (owner only; step-up and a reason, 26 §3.2). An owner invites
// a new admin by email with a role, and the invite runs out after 48 hours. Removing an admin ends
// their access at once. The server checks all of it; this page only asks.
import type { SupabaseClient } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';
import { verifyCode } from '../auth/mfa';
import { messageFor, runCommand, type CommandResult } from '../lib/commands';

export const ROLE_WORDS: Record<string, string> = {
  owner: 'Owner',
  moderator: 'Moderator',
  support: 'Support',
  analyst: 'Analyst (read-only)',
};

interface AdminRow {
  id: string;
  role: string;
  active: boolean;
  mfa_enabled: boolean;
  invite_expires_at: string | null;
  accepted_at: string | null;
}

/** What the list says about each admin. */
export function adminState(a: AdminRow, now: number): string {
  if (!a.active) return 'Removed';
  if (a.accepted_at || a.mfa_enabled) return 'Active';
  if (a.invite_expires_at && Date.parse(a.invite_expires_at) <= now) return 'Invite ran out';
  return 'Invite sent, not accepted yet';
}

export async function sendInvite(
  sb: SupabaseClient,
  email: string,
  role: string,
  reason: string,
): Promise<CommandResult> {
  const { data, error } = await sb.functions.invoke('admin-invite', {
    body: { email, role, reason, idempotency_key: crypto.randomUUID() },
  });
  if (!error && data && typeof data === 'object') return data as CommandResult;
  // A refusal comes back as an HTTP error whose body is the function's own answer.
  const ctx = (error as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  const answer = ctx?.json ? await ctx.json().catch(() => null) : null;
  if (answer && typeof answer === 'object' && 'ok' in answer) return answer as CommandResult;
  return { ok: false, error: 'invite_not_available' }; // not deployed yet, or not reachable
}

export function AdminsPage({ sb }: { sb: SupabaseClient }) {
  const [rows, setRows] = useState<AdminRow[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('analyst');
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [msg, setMsg] = useState('');
  const load = () => {
    void sb
      .schema('ops')
      .from('admin_users')
      .select('id,role,active,mfa_enabled,invite_expires_at,accepted_at')
      .order('created_at')
      .then(({ data }) => setRows((data ?? []) as AdminRow[]));
  };
  useEffect(load, [sb]);

  const stepUp = async () => {
    if (!needCode) return true;
    if (!(await verifyCode(sb, code.trim()))) {
      setMsg('That code did not work. Wait for a new code, then try again.');
      return false;
    }
    await sb.auth.refreshSession();
    return true;
  };
  const after = (r: CommandResult) => {
    setNeedCode(r.error === 'step_up_required');
    setMsg(
      r.error === 'invite_not_available'
        ? 'Invites are not switched on yet. Ask Claude to set up the invite step.'
        : r.error === 'player_account_cannot_be_admin'
          ? 'That email already has a player account. Use a different email for the admin account.'
          : messageFor(r),
    );
    if (r.ok) load();
  };

  return (
    <section aria-labelledby="a">
      <h2 id="a">Admin users</h2>
      <table>
        <thead>
          <tr>
            <th scope="col">Role</th>
            <th scope="col">State</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td>{ROLE_WORDS[a.role] ?? a.role}</td>
              <td>{adminState(a, Date.now())}</td>
              <td>
                {a.active && (
                  <button
                    onClick={async () => {
                      if (!reason.trim()) return setMsg('Write a short reason first.');
                      if (await stepUp())
                        after(
                          await runCommand(sb, 'admin.remove', { admin_user_id: a.id }, reason),
                        );
                    }}
                  >
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (await stepUp()) after(await sendInvite(sb, email.trim(), role, reason.trim()));
        }}
      >
        <h3>Invite an admin</h3>
        <p className="muted">They get an email. The invite runs out after 48 hours.</p>
        <label htmlFor="invite-email">Their email</label>
        <input
          id="invite-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <label htmlFor="invite-role">Role</label>
        <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value)}>
          {Object.entries(ROLE_WORDS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <label htmlFor="admin-reason">Reason (for an invite or a removal)</label>
        <input
          id="admin-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        {needCode && (
          <>
            <label htmlFor="admin-code">6-digit code</label>
            <input
              id="admin-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </>
        )}
        <p>
          <button className="primary" type="submit">
            Send the invite
          </button>
        </p>
        <p role="status">{msg}</p>
      </form>
    </section>
  );
}
